import React from 'react';
import { EMAIL_REGEX, ESIC_IP_NUMBER_REGEX, PERSONAL_PAN_REGEX, PF_UAN_REGEX } from 'utils/regexes';
import { ValidationSchema } from 'forms';
import {
  BasicDetailsFormFields,
  CompensationFormFields,
  ReviewDetailsFormFields,
  StatutoryFormFields,
  ReviewDetailSection,
} from './types';
import {
  CheckCircleIcon,
  StepItemIcon,
  StepItemIndicator,
  StepItemProps,
} from '@razorpay/blade/components';
import { portalStatus } from 'components/EmployeeDetails/StatutoryContributions/data';
import { GET_DRAFT_EMPLOYEE, initialCompensationData, initialComplianceData, steps } from './data';
import { QueryClient } from 'react-query';
import { SalaryStructureComponent } from './components/salaryStructure/constants';
import {
  CompensationStructure,
  PayrollGetDraftEmployeeResponse,
} from 'api/gql-utils/employeeOnboardingGql/types';
import { PortalStatus } from 'components/EmployeeDetails/StatutoryContributions/types';
import { parse } from 'date-fns';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { getStateName } from '../EmployeeSelfOnboarding/utils';
import { OfficeLocationResponse } from 'api/types/officeLocationTypes';

const validateEmail = (email: string): boolean => {
  return new RegExp(EMAIL_REGEX).test(email);
};

const validatePAN = (pan: string): boolean => {
  return PERSONAL_PAN_REGEX.test(pan);
};

const validateUAN = (uan: string): boolean => {
  return PF_UAN_REGEX.test(uan);
};

const validateESICIP = (ip: string): boolean => {
  return ESIC_IP_NUMBER_REGEX.test(ip);
};

export const validationSchema: ValidationSchema<BasicDetailsFormFields> = {
  employeeName: (formData) => (!formData.employeeName?.trim() ? 'Employee name is required' : null),
  emailId: (formData) => {
    if (formData.emailNotPresent) {
      return null;
    }
    if (formData.emailId?.trim() && !validateEmail(formData.emailId)) {
      return 'Please enter a valid email address';
    }
    if (!formData.emailId?.trim()) {
      return 'Email address is required';
    }
    return null;
  },
  hireDate: (formData) => (!formData.hireDate ? 'Hire date is required' : null),
  designation: (formData) => (!formData.designation?.trim() ? 'Designation is required' : null),
  officeLocation: (formData) =>
    !formData.officeLocation?.length ? 'Office location is required' : null,
};

export const statutoryValidationSchema: ValidationSchema<StatutoryFormFields> = {
  pan: (formData) => {
    if (formData.pan && !validatePAN(formData.pan)) {
      return 'Please enter a valid PAN number';
    }
    return null;
  },
  pfStatus: (formData) => {
    if (formData.isPfEnabled && !formData.pfStatus?.length) {
      return 'Please select PF status';
    }
    return null;
  },
  pfUan: (formData) => {
    if (formData.isPfEnabled && formData.pfUan && !validateUAN(formData.pfUan)) {
      return 'Please enter valid PF UAN number';
    }
    return null;
  },
  esicStatus: (formData) => {
    if (formData.isEsicEnabled && !formData.esicStatus?.length) {
      return 'Please select ESIC status';
    }
    return null;
  },
  esicIpNumber: (formData) => {
    if (formData.isEsicEnabled && formData.esicIpNumber && !validateESICIP(formData.esicIpNumber)) {
      return 'Please enter valid ESIC IP number';
    }
    return null;
  },
  ptState: (formData) => {
    if (formData.isPtEnabled && !formData.ptState?.length) {
      return 'Please select PT state';
    }
    return null;
  },
  lwfLocation: (formData) => {
    if (formData.isLwfEnabled && !formData.lwfLocation?.length) {
      return 'Please select LWF location';
    }
    return null;
  },
};

export const statutoryValidationSchemaWithoutEmail: ValidationSchema<StatutoryFormFields> = {
  pan: (formData) => {
    if (!formData.pan) {
      return 'Please enter PAN number';
    }
    if (!validatePAN(formData.pan)) {
      return 'Please enter a valid PAN number';
    }
    return null;
  },
  pfStatus: (formData) => {
    if (formData.isPfEnabled && !formData.pfStatus?.length) {
      return 'Please select PF status';
    }
    return null;
  },
  pfUan: (formData) => {
    if (
      formData.isPfEnabled &&
      formData.pfStatus?.[0] === portalStatus.registered &&
      !formData.pfUan
    ) {
      return 'Please enter PF UAN number';
    }
    if (
      formData.isPfEnabled &&
      formData.pfStatus?.[0] === portalStatus.registered &&
      formData.pfUan &&
      !validateUAN(formData.pfUan)
    ) {
      return 'Please enter valid PF UAN number';
    }
    return null;
  },
  esicStatus: (formData) => {
    if (formData.isEsicEnabled && !formData.esicStatus?.length) {
      return 'Please select ESIC status';
    }
    return null;
  },
  esicIpNumber: (formData) => {
    if (
      formData.isEsicEnabled &&
      formData.esicStatus?.[0] === portalStatus.registered &&
      !formData.esicIpNumber
    ) {
      return 'Please enter ESIC IP number';
    }
    if (
      formData.isEsicEnabled &&
      formData.esicStatus?.[0] === portalStatus.registered &&
      formData.esicIpNumber &&
      !validateESICIP(formData.esicIpNumber)
    ) {
      return 'Please enter valid ESIC IP number';
    }
    return null;
  },
  ptState: (formData) => {
    if (formData.isPtEnabled && !formData.ptState?.length) {
      return 'Please select PT state';
    }
    return null;
  },
  lwfLocation: (formData) => {
    if (formData.isLwfEnabled && !formData.lwfLocation?.length) {
      return 'Please select LWF location';
    }
    return null;
  },
};

export const getAmountCleanedValue = (value?: string) => {
  if (!value) {
    return value;
  }
  return value.toString().replace(/,/g, '');
};

export const getAmountPayload = (value?: string) => {
  const cleanValue = getAmountCleanedValue(value);
  return cleanValue ? Number(cleanValue) : 0;
};

export const compensationValidationSchema: ValidationSchema<CompensationFormFields> = {
  annualCtc: (formData) => {
    if (formData.salaryStructureType?.[0] === 'custom') {
      return null;
    }
    const cleanedValue = getAmountCleanedValue(formData.annualCtc);

    if (cleanedValue && isNaN(Number(cleanedValue))) {
      return 'Annual CTC must be a valid number';
    }

    if (cleanedValue && !Number.isInteger(Number(cleanedValue))) {
      return 'Annual CTC cannot have decimal value';
    }

    if (!(Number(cleanedValue) >= 0)) {
      return 'Annual CTC should be greater than or equal to 0';
    }

    return null;
  },
  variablePay: (formData) => {
    if (!formData.variablePay) {
      return null;
    }
    const cleanedValue = getAmountCleanedValue(formData.variablePay);

    if (cleanedValue && isNaN(Number(cleanedValue))) {
      return 'Variable Pay must be a valid number';
    }

    if (cleanedValue && !Number.isInteger(Number(cleanedValue))) {
      return 'Variable Pay cannot have decimal value';
    }

    if (!(Number(cleanedValue) >= 0)) {
      return 'Variable Pay should be greater than or equal to 0';
    }
    return null;
  },
};

export const getStepProgress = (
  stepIndex: number,
  currentStepIndex: number,
): 'start' | 'full' | 'none' => {
  if (stepIndex < currentStepIndex) {
    return 'full';
  } else if (stepIndex === currentStepIndex) {
    return 'start';
  } else {
    return 'none';
  }
};

export const getStepIndicator = (
  stepIndex: number,
  currentStepIndex: number,
): StepItemProps['marker'] => {
  if (stepIndex < currentStepIndex) {
    return <StepItemIcon icon={CheckCircleIcon} color="positive" />;
  }
  if (stepIndex === currentStepIndex) {
    return <StepItemIndicator color="primary" />;
  }
  return <StepItemIndicator color="neutral" />;
};

export const invalidateDraftEmployeeCache = (queryClient: QueryClient, draftEmployeeId: number) => {
  queryClient.invalidateQueries([GET_DRAFT_EMPLOYEE, draftEmployeeId]);
};

type DefaultComponents = Omit<CompensationStructure, 'customAllowances'>;

export const getCompensationStepPayload = (data: SalaryStructureComponent[]) => {
  const defaultComponents: DefaultComponents = data
    .filter(({ type }) => type === 'default')
    .reduce((acc, component) => {
      acc[component.id as keyof DefaultComponents] = Number(component.amount);
      return acc;
    }, {} as DefaultComponents);

  const customAllowances: CompensationStructure['customAllowances'] = data
    .filter(({ type, name }) => type === 'customAllowance' && !!name.trim())
    .map((component) => ({
      name: component.name,
      value: Number(component.amount),
      taxable: component.taxable || 'no',
    }));

  const payload: CompensationStructure = {
    ...defaultComponents,
    customAllowances: customAllowances,
  };

  return payload;
};

export const updateInitialBasicFormData = ({
  data,
  setFormData,
  isEsicMultiLocationEnabled,
}: {
  data: PayrollGetDraftEmployeeResponse['payrollGetDraftEmployee']['data'];
  setFormData?: (formData: Partial<BasicDetailsFormFields>) => void;
  isEsicMultiLocationEnabled?: boolean;
}): Partial<BasicDetailsFormFields> => {
  const transformedData = {
    employeeName: data.basicDetails?.name,
    emailId: data.basicDetails?.emailNotPresent ? '' : data.basicDetails?.email,
    emailNotPresent: data.basicDetails?.emailNotPresent ?? false,
    hireDate: data.basicDetails?.hireDate
      ? parse(data.basicDetails.hireDate, dateFormats.yearMonthDate, new Date())
      : null,
    designation: data.basicDetails?.designation,
    department: data.basicDetails?.department,
    manager_people_id: data.basicDetails?.managerPeopleId,
    officeLocation: isEsicMultiLocationEnabled
      ? data.basicDetails?.officeLocationId
        ? [data.basicDetails?.officeLocationId.toString()]
        : []
      : data.basicDetails?.location
      ? [data.basicDetails?.location.toString()]
      : [],
  };
  setFormData?.(transformedData);
  return transformedData;
};

export const updateInitialCompensationFormData = (
  data: PayrollGetDraftEmployeeResponse['payrollGetDraftEmployee']['data'],
  setFormData?: (formData: Partial<CompensationFormFields>) => void,
): Partial<CompensationFormFields> => {
  const transformedData = {
    salaryStructureType: data.compensationDetails?.salaryStructureType
      ? [data.compensationDetails?.salaryStructureType]
      : initialCompensationData.salaryStructureType,
    annualCtc:
      data.compensationDetails?.ctc !== undefined && data.compensationDetails?.ctc !== null
        ? String(data.compensationDetails?.ctc)
        : initialCompensationData.annualCtc,
    variablePay:
      data.compensationDetails?.variablePay !== undefined &&
      data.compensationDetails?.variablePay !== null
        ? String(data.compensationDetails?.variablePay)
        : initialCompensationData.variablePay,
  };
  setFormData?.(transformedData);
  return transformedData;
};

export const updateInitialStatutoryFormData = (
  data: PayrollGetDraftEmployeeResponse['payrollGetDraftEmployee']['data'],
  setFormData?: (formData: StatutoryFormFields) => void,
): Partial<StatutoryFormFields> => {
  const complianceDetails = data.complianceDetails;
  const statutoryDetails = data.userStatutoryDetails;

  if (complianceDetails || statutoryDetails) {
    const transformedData = {
      pan: statutoryDetails?.panNumber ?? initialComplianceData.pan,
      isPfEnabled: complianceDetails?.isPf ?? initialComplianceData.isPfEnabled,
      pfStatus: statutoryDetails?.pfStatus
        ? [statutoryDetails.pfStatus as PortalStatus]
        : initialComplianceData.pfStatus,
      pfUan: statutoryDetails?.pfUan ?? initialComplianceData.pfUan,
      isEsicEnabled: complianceDetails?.isEsi ?? initialComplianceData.isEsicEnabled,
      esicStatus: statutoryDetails?.esicStatus
        ? [statutoryDetails.esicStatus as PortalStatus]
        : initialComplianceData.esicStatus,
      esicIpNumber: statutoryDetails?.esicIp ?? initialComplianceData.esicIpNumber,
      isPtEnabled: complianceDetails?.isPt ?? initialComplianceData.isPtEnabled,
      ptState: statutoryDetails?.locationPt
        ? [statutoryDetails.locationPt.toString()]
        : initialComplianceData.ptState,
      isLwfEnabled: complianceDetails?.isLwf ?? initialComplianceData.isLwfEnabled,
      lwfLocation: statutoryDetails?.locationLwf
        ? [statutoryDetails.locationLwf.toString()]
        : initialComplianceData.lwfLocation,
    };
    setFormData?.(transformedData);
    return transformedData;
  }

  return initialComplianceData;
};

export const getReviewDetailsFormData = (
  data: PayrollGetDraftEmployeeResponse['payrollGetDraftEmployee']['data'],
  isEsicMultiLocationEnabled: boolean,
): Partial<ReviewDetailsFormFields> => {
  const basicDetails = updateInitialBasicFormData({ data, isEsicMultiLocationEnabled });
  const compensationDetails = updateInitialCompensationFormData(data);
  const statutoryDetails = updateInitialStatutoryFormData(data);
  return {
    ...basicDetails,
    ...compensationDetails,
    ...statutoryDetails,
  };
};

export const getRequiredFieldValidationSchema = (
  hasEmail: boolean,
): ValidationSchema<ReviewDetailsFormFields> => {
  return {
    ...validationSchema,
    ...(hasEmail ? statutoryValidationSchema : statutoryValidationSchemaWithoutEmail),
    ...compensationValidationSchema,
  };
};

export const canCreateEmployee = (
  formData: Partial<ReviewDetailsFormFields>,
  validationSchema: ValidationSchema<ReviewDetailsFormFields>,
) => {
  let isValid = true;
  const validateKeys = Object.keys(validationSchema);
  for (const key of validateKeys) {
    const validatorFn = validationSchema[key as keyof typeof validationSchema];
    const hasError = validatorFn ? Boolean(validatorFn(formData)) : false;
    if (hasError) {
      isValid = false;
      break;
    }
  }
  return isValid;
};

export const getReviewDetailsSections = (
  data?: PayrollGetDraftEmployeeResponse['payrollGetDraftEmployee']['data'],
  selectedOfficeLocation?: OfficeLocationResponse,
) => {
  const sections: ReviewDetailSection[] = [
    {
      title: 'Basic Details',
      stepRoute: steps[0].route,
      items: [
        { label: 'Name', value: data?.basicDetails?.name, fieldKey: 'employeeName' },
        {
          label: 'Email ID',
          value: data?.basicDetails?.emailNotPresent ? '-' : data?.basicDetails?.email,
          fieldKey: 'emailId',
        },
        {
          label: 'Joining Date',
          value: data?.basicDetails?.hireDate
            ? getFormattedDateValue({
                date: parse(data?.basicDetails?.hireDate, dateFormats.yearMonthDate, new Date()),
                formatString: dateFormats.dateMonthYearWithSlash,
              })
            : null,
          fieldKey: 'hireDate',
        },
        { label: 'Designation', value: data?.basicDetails?.designation, fieldKey: 'designation' },
        { label: 'Department', value: data?.basicDetails?.department, fieldKey: 'department' },
        { label: 'Manager', value: data?.basicDetails?.managerName, fieldKey: 'manager_people_id' },
        {
          label: 'Location',
          value: selectedOfficeLocation
            ? selectedOfficeLocation.office_name
            : getStateName(data?.basicDetails?.location),
          fieldKey: 'officeLocation',
        },
      ],
    },
    {
      title: 'Compensation Details',
      stepRoute: steps[1].route,
      items:
        data?.compensationDetails?.salaryStructureType === 'custom'
          ? [
              {
                label: 'Salary Structure',
                value: data?.compensationDetails?.salaryStructureType,
                fieldKey: 'salaryStructureType',
              },
              {
                label: 'Variable Pay',
                value: data?.compensationDetails?.variablePay || 0,
                type: 'amount',
                fieldKey: 'variablePay',
              },
            ]
          : [
              {
                label: 'Salary Structure',
                value: data?.compensationDetails?.salaryStructureType,
                fieldKey: 'salaryStructureType',
              },
              {
                label: 'Annual CTC',
                value: data?.compensationDetails?.ctc || 0,
                type: 'amount',
                fieldKey: 'annualCtc',
              },
              {
                label: 'Variable Pay',
                value: data?.compensationDetails?.variablePay || 0,
                type: 'amount',
                fieldKey: 'variablePay',
              },
            ],
    },
    {
      title: 'Compliance Details',
      stepRoute: steps[2].route,
      items: [
        { label: 'TDS', value: 'Enabled' },
        { label: 'PAN', value: data?.userStatutoryDetails?.panNumber, fieldKey: 'pan' },
        {
          label: 'Provident Fund',
          value: data?.complianceDetails?.isPf ? 'Enabled' : 'Disabled',
          fieldKey: 'isPfEnabled',
        },
        {
          label: 'PF Status',
          value: data?.userStatutoryDetails?.pfStatus,
          fieldKey: 'pfStatus',
          shouldHide: !data?.complianceDetails?.isPf,
        },
        {
          label: 'PF UAN',
          value: data?.userStatutoryDetails?.pfUan,
          fieldKey: 'pfUan',
          shouldHide: !(
            data?.complianceDetails?.isPf &&
            data?.userStatutoryDetails?.pfStatus === portalStatus.registered
          ),
        },
        {
          label: 'ESIC',
          value: data?.complianceDetails?.isEsi ? 'Enabled' : 'Disabled',
          fieldKey: 'isEsicEnabled',
        },
        {
          label: 'ESIC Status',
          value: data?.userStatutoryDetails?.esicStatus,
          fieldKey: 'esicStatus',
          shouldHide: !data?.complianceDetails?.isEsi,
        },
        {
          label: 'ESIC IP Number',
          value: data?.userStatutoryDetails?.esicIp,
          fieldKey: 'esicIpNumber',
          shouldHide: !(
            data?.complianceDetails?.isEsi &&
            data?.userStatutoryDetails?.esicStatus === portalStatus.registered
          ),
        },
        {
          label: 'Professional Tax',
          fieldKey: 'isPtEnabled',
          value: data?.complianceDetails?.isPt ? 'Enabled' : 'Disabled',
        },
        {
          label: 'Labour Welfare Fund',
          fieldKey: 'isLwfEnabled',
          value: data?.complianceDetails?.isLwf ? 'Enabled' : 'Disabled',
        },
      ],
    },
  ];

  return sections;
};
